import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Modal, Field, TextArea, Button } from "@/components/ui-kit";
import { toast } from "sonner";
import { Plus, CaretRight, MagnifyingGlass, Buildings } from "@phosphor-icons/react";

const empty = { name: "", company: "", email: "", phone: "", address: "", notes: "" };

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/customers").then(({ data }) => setCustomers(data)), []);
  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setOpen(false);
      setForm(empty);
      load();
    } catch {
      toast.error("Failed to add customer");
    }
  };

  const filtered = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(q.toLowerCase()) ||
      (c.company || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle={`${customers.length} total`}
        action={
          <Button data-testid="add-customer-button" onClick={() => setOpen(true)}>
            <Plus size={16} weight="bold" /> Add Customer
          </Button>
        }
      />
      <div className="p-8 space-y-4">
        <div className="relative max-w-sm">
          <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="customer-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customers…"
            className="w-full border border-slate-300 rounded-lg pl-10 pr-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#F49B1C]/60 focus:border-[#F49B1C]"
          />
        </div>

        <div className="card-soft overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 font-mono text-xs uppercase tracking-wide">
                <th className="text-left px-5 py-3.5 font-semibold">Name</th>
                <th className="text-left px-5 py-3.5 font-semibold">Company</th>
                <th className="text-left px-5 py-3.5 font-semibold">Phone</th>
                <th className="text-left px-5 py-3.5 font-semibold">Open Tasks</th>
                <th className="px-5 py-3.5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`customer-row-${c.id}`}
                  onClick={() => navigate(`/customers/${c.id}`)}
                  className="border-b border-slate-100 last:border-0 hover:bg-orange-50/40 cursor-pointer transition-colors"
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#16205A]/5 flex items-center justify-center">
                        <Buildings size={18} weight="fill" className="text-[#16205A]" />
                      </div>
                      <span className="font-semibold text-sm text-slate-800">{c.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-500">{c.company || "—"}</td>
                  <td className="px-5 py-4 font-mono text-sm text-slate-500">{c.phone || "—"}</td>
                  <td className="px-5 py-4" data-testid={`customer-tasks-${c.id}`}>
                    {c.open_tasks > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {c.todo_tasks > 0 && (
                          <span className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: "#E2E8F0", color: "#334155" }}>
                            {c.todo_tasks} TO DO
                          </span>
                        )}
                        {c.in_progress_tasks > 0 && (
                          <span className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: "#FEF3C7", color: "#B45309" }}>
                            {c.in_progress_tasks} IN PROGRESS
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">No open tasks</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right"><CaretRight size={16} weight="bold" className="text-slate-300" /></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-sm text-slate-400">No customers found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Customer">
        <form onSubmit={save} className="space-y-4">
          <Field label="Name *" data-testid="customer-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field label="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Field label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <Field label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <TextArea label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-customer-button">Save</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
